import { useEffect, useState, useCallback } from 'react';
import { differenceInWeeks, parseISO } from 'date-fns';
import { supabase } from '../lib/supabase';

const WEEKS_IN_YEAR = 52;
const LIFE_EXPECTANCY = 80;
const TOTAL_WEEKS = WEEKS_IN_YEAR * LIFE_EXPECTANCY;
const ANIMATION_SPEED = 0.2;

function LifeInWeeks() {
  const [birthday, setBirthday] = useState<string | null>(null);
  const [filledWeeks, setFilledWeeks] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBirthday();
  }, []);

  const fetchBirthday = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setError('Please sign in to continue');
        setLoading(false);
        return;
      }

      const { data, error: fetchError } = await supabase
        .from('users')
        .select('birthday')
        .eq('id', user.id)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      if (data?.birthday) {
        setBirthday(data.birthday);
        const weeksLived = calculateWeeksLived(data.birthday);
        startAnimation(weeksLived);
      }
    } catch (err) {
      console.error('Error fetching birthday:', err);
      setError('Failed to fetch birthday');
    } finally {
      setLoading(false);
    }
  };

  const calculateWeeksLived = (birthDate: string) => {
    const birth = parseISO(birthDate);
    const today = new Date();
    return Math.max(0, differenceInWeeks(today, birth));
  };

  const startAnimation = useCallback((targetWeeks: number) => {
    setIsAnimating(true);
    setFilledWeeks(0);
    
    let currentWeek = 0;
    const animate = () => {
      if (currentWeek <= targetWeeks) {
        setFilledWeeks(currentWeek);
        currentWeek += 4;
        setTimeout(animate, ANIMATION_SPEED);
      } else {
        setFilledWeeks(targetWeeks);
        setIsAnimating(false);
      }
    };

    setTimeout(animate, ANIMATION_SPEED);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-gray-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-gray-600 mb-6">{error}</p>
          <a
            href="/"
            className="text-sm text-gray-900 hover:text-gray-600 transition-colors"
          >
            Return to Sign In
          </a>
        </div>
      </div>
    );
  }

  if (!birthday) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-gray-600 mb-6">Please set your birthday to continue</p>
          <a
            href="/profile"
            className="text-sm text-gray-900 hover:text-gray-600 transition-colors"
          >
            Go to Profile
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white rounded-2xl flex items-center justify-center">
      <div className="w-[90vmin] h-[90vmin] relative">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-light text-gray-900 mb-2">Your Life in Weeks</h1>
          <p className="text-sm text-gray-500 tracking-wide">Each square represents one week</p>
        </div>
        
        <div className="relative w-full h-[calc(100%-4rem)] overflow-hidden bg-gray-50">
          <div 
            className="grid gap-[1px] w-full h-full"
            style={{ 
              gridTemplateColumns: `repeat(${WEEKS_IN_YEAR}, 1fr)`,
              gridAutoRows: '1fr',
            }}
          >
            {Array.from({ length: TOTAL_WEEKS }, (_, i) => (
              <div
                key={i}
                className={`transition-colors duration-300 ${
                  i < filledWeeks ? 'bg-gray-900' : 'bg-gray-100'
                }`}
              />
            ))}
          </div>
          <div className="absolute bottom-0 left-0 right-0 bg-white/90 backdrop-blur-sm py-2 text-center">
            <p className="text-sm text-gray-900 font-light">
              {isAnimating ? (
                'Visualizing your journey...'
              ) : filledWeeks > 0 ? (
                `${Math.floor(filledWeeks / 52)} years, ${filledWeeks % 52} weeks lived`
              ) : (
                'Ready to begin visualization'
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LifeInWeeks;